import { computed, markRaw, reactive, ref, watch, type Component } from 'vue'
import { defineStore } from 'pinia'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useGlobalStore } from '@/stores/globalStore'
import { useLocalStorage } from '@/lib/utils/useLocalStorage'
import { isStandaloneEnv } from '@/lib/utils/env'
import { Permissions, isMenuDivider } from '@/lib/types'
import type {
  DrawerEventHandler,
  DrawerEventType,
  DrawerPlacement,
  DrawerState,
  MenuEntry,
  MenuItem,
} from '@/lib/types'

/**
 * UI state — sidebar visibility, expanded menu groups, the
 * permission-gated menu tree, and the two drawer slots (right +
 * bottom).
 *
 * `menuItems` is computed from the authed user's permission set
 * and the current route. Each item declares the `permissions` it
 * needs; items the actor can't see are filtered out. Adding a new
 * admin surface = add a `MenuItem` here + register the route in
 * `router/routes/auth.ts`.
 *
 * ## Drawer surface (two-slot, intentionally minimal)
 *
 * `drawer` is a two-slot reactive: `right` and `bottom`. Each slot
 * is its own `{ isOpen, body }` pair. The slots are independent —
 * opening one does not touch the other. Both can be open at the
 * same time. Bottom takes z-index precedence over right when both
 * are open (still under PV modals/dialogs).
 *
 * `openDrawer(body, opts?)` defaults `opts.placement = 'right'` so
 * pre-existing single-arg callers continue to open the right
 * drawer (the historical default). Title / mode / save+cancel
 * handlers / canSave gate / saving spinner all live INSIDE the
 * body component (typically composed via the shared `<DrawerCard>`
 * chrome primitive). The host doesn't know or care about those
 * concerns.
 *
 * Why store-driven + dumb host (not Teleport + slots): Teleport
 * into a target rendered by the SAME Vue tree has lifecycle /
 * patch-order races across `<RouterView>` + async-component
 * boundaries (`__vnode null`, `parentNode null` errors). Store-
 * driven content has clean lifecycle — the body is a plain
 * `<component :is>` mounted by the layout.
 */
export const useUIStore = defineStore('ui', () => {
  const globalStore = useGlobalStore()
  const route = useRoute()
  const { t } = useI18n()

  /**
   * Sidebar visibility model — TWO independent dimensions:
   *
   *   `sidebarMobileOpen` — drawer-style overlay state on small
   *     viewports (`<md`). Default false (closed). Toggled by the
   *     hamburger button in `Header.vue`. Auto-closes on route nav
   *     (so clicking a menu item dismisses the drawer naturally).
   *
   *   `sidebarCompact` — desktop-mode icons-only collapse (`md+`).
   *     Default false (full labels). Toggled by the chevron
   *     decorator straddling the sidebar/main divider. Persisted to
   *     localStorage so a user's preference survives reload.
   *
   * Why two flags instead of one tri-state: mobile + desktop are
   * orthogonal — a user can have the desktop sidebar in compact
   * mode AND the mobile drawer open simultaneously across viewport
   * transitions. Treating them independently means each input
   * (hamburger / decorator) owns one bit and never has to reason
   * about the other.
   */
  // Existing users with this key pre-`useLocalStorage` had a `'1'`/`'0'`
  // string-encoded boolean. The new composable JSON-encodes booleans —
  // legacy values fail `JSON.parse` and gracefully reset to default
  // (false). One-time preference reset per user; acceptable.
  const SIDEBAR_COMPACT_KEY = 'lockerserver:sidebar-compact'

  const sidebarMobileOpen = ref<boolean>(false)
  const sidebarCompact = useLocalStorage<boolean>(SIDEBAR_COMPACT_KEY, false)

  const expandedMenus = ref<Record<string, boolean>>({})

  /**
   * Route-transition busy flag. Set by `router.beforeEach`, cleared
   * by `afterEach` + `onError`. `<NavigationSplash />` watches this
   * and renders the full-viewport overlay after a short debounce so
   * instant navigations don't flash. Single boolean, no counter —
   * Vue Router serializes navigations (one in-flight at a time).
   */
  const navigating = ref<boolean>(false)

  const drawer = reactive<DrawerState>({
    right: { isOpen: false, body: null },
    bottom: { isOpen: false, body: null },
  })

  // Legacy alias — older call sites still reference `ui.drawerOpen`
  // (NavigationSplash watcher, tests). True if EITHER slot is open.
  const drawerOpen = computed<boolean>(
    () => drawer.right.isOpen || drawer.bottom.isOpen,
  )

  /**
   * Per-placement drawer event subscribers. Outer key = placement;
   * inner key = event. A body component subscribes to ITS slot's
   * events via `useDrawerEvents(placement)` — opening or closing
   * the OTHER slot doesn't notify it.
   *
   * Module-scoped (NOT inside `reactive`) — listener registry isn't
   * UI state, no need for Vue to track it.
   */
  const drawerListeners: Record<DrawerPlacement, Map<DrawerEventType, Set<DrawerEventHandler>>> = {
    right: new Map(),
    bottom: new Map(),
  }

  /**
   * Register a listener for a drawer lifecycle event on a specific
   * placement. Returns an unsubscribe function. Most call sites
   * should use the `useDrawerEvents(placement)` composable instead,
   * which wires the unsubscribe to the calling component's effect
   * scope.
   */
  const onDrawerEvent = (
    placement: DrawerPlacement,
    event: DrawerEventType,
    handler: DrawerEventHandler,
  ): (() => void) => {
    const bucket = drawerListeners[placement]
    let handlers = bucket.get(event)
    if (!handlers) {
      handlers = new Set()
      bucket.set(event, handlers)
    }
    handlers.add(handler)
    return () => {
      bucket.get(event)?.delete(handler)
    }
  }

  const emitDrawerEvent = (placement: DrawerPlacement, event: DrawerEventType): void => {
    const handlers = drawerListeners[placement].get(event)
    if (!handlers || handlers.size === 0) return
    // Snapshot to a list before iterating — handlers may
    // unsubscribe themselves during invocation, which would mutate
    // the Set mid-iteration.
    Array.from(handlers).forEach((h) => {
      try {
        h()
      } catch (e) {
        // Swallow per-handler errors so one bad listener doesn't
        // break the others. Surface via console so issues are
        // visible during development.
        // eslint-disable-next-line no-console
        console.error('[uiStore] drawer event handler threw:', e)
      }
    })
  }

  const openSidebarMobile = (): void => {
    sidebarMobileOpen.value = true
  }
  const closeSidebarMobile = (): void => {
    sidebarMobileOpen.value = false
  }
  const toggleSidebarMobile = (): void => {
    sidebarMobileOpen.value = !sidebarMobileOpen.value
  }
  const toggleSidebarCompact = (): void => {
    sidebarCompact.value = !sidebarCompact.value
  }

  /**
   * Viewport-aware single-entry sidebar toggle. The hamburger in
   * `Header.vue` calls this; behavior switches on the viewport:
   *   `md+`  → toggle the desktop compact mode (icons-only)
   *   `<md`  → toggle the mobile drawer overlay
   * The decorator chevron on the sidebar/main divider stays bound
   * directly to `toggleSidebarCompact` (it's desktop-only). The
   * matchMedia check uses Tailwind's `md` breakpoint (768px) so
   * the dispatch matches every other responsive class on the page.
   */
  const MD_QUERY = '(min-width: 768px)'
  const toggleSidebar = (): void => {
    if (typeof window !== 'undefined' && window.matchMedia(MD_QUERY).matches) {
      toggleSidebarCompact()
    } else {
      toggleSidebarMobile()
    }
  }

  // Auto-close the mobile sidebar drawer when the route changes.
  // Operator clicks a nav item → route changes → drawer dismisses
  // without an extra tap. Has no effect on desktop (the desktop
  // sidebar is in-flow, not a drawer; `sidebarMobileOpen` is just
  // ignored at `md+` viewports).
  watch(
    () => route.fullPath,
    (_, prev) => {
      if (prev === undefined) return
      if (sidebarMobileOpen.value) sidebarMobileOpen.value = false
    },
  )

  const toggleMenu = (key: string): void => {
    expandedMenus.value = { ...expandedMenus.value, [key]: !expandedMenus.value[key] }
  }

  const startNavigation = (): void => {
    navigating.value = true
  }

  const finishNavigation = (): void => {
    navigating.value = false
  }

  /**
   * Show the drawer with `body` rendered inside. Defaults to the
   * `'right'` slot when `opts.placement` is omitted — keeps every
   * pre-two-slot caller working unchanged. Pass
   * `{ placement: 'bottom' }` to open the bottom slot. The two
   * slots are independent: opening `'bottom'` does NOT close
   * `'right'` (and vice versa); they may both be open at once.
   *
   * The body component owns everything else (title, confirm handler,
   * validation, etc.) — typically by composing `<DrawerCard>` for
   * the standard chrome.
   *
   * `markRaw` keeps Pinia's reactivity proxy from walking the Vue
   * component object.
   *
   * Emits `'open'` for the targeted placement ONLY on the
   * `isOpen: false → true` transition for THAT slot. Calling
   * `openDrawer` while that slot is already open (e.g., to swap
   * bodies) updates `body` but does NOT re-emit `'open'`.
   */
  const openDrawer = (
    body: Component,
    opts?: { placement?: DrawerPlacement },
  ): void => {
    const placement: DrawerPlacement = opts?.placement ?? 'right'
    const slot = drawer[placement]
    const wasClosed = !slot.isOpen
    slot.body = markRaw(body)
    slot.isOpen = true
    if (wasClosed) emitDrawerEvent(placement, 'open')
  }

  /**
   * Close one drawer. Without an arg, closes the TOPMOST open slot
   * (Esc semantics — bottom takes z-index precedence, so it peels
   * off first). Pass an explicit placement for surgical close. Use
   * `closeAllDrawers()` for cross-cutting events that should reset
   * both slots (route nav, tab switch).
   */
  const closeDrawer = (placement?: DrawerPlacement): void => {
    const target: DrawerPlacement | null =
      placement
        ? (drawer[placement].isOpen ? placement : null)
        : drawer.bottom.isOpen
          ? 'bottom'
          : drawer.right.isOpen
            ? 'right'
            : null
    if (target === null) return
    drawer[target].isOpen = false
    // Don't null out `body` immediately — it's rendered during the
    // close animation. Clearing happens on the next open.
    emitDrawerEvent(target, 'close')
  }

  /**
   * Close every open drawer slot. Used by host-level cross-cutting
   * dismissal hooks: route change (`DrawerHost`'s route watcher),
   * in-page nav (`useCloseDrawerOn`'s tab-change use). Each closing
   * slot emits its own `'close'` event.
   */
  const closeAllDrawers = (): void => {
    if (drawer.bottom.isOpen) closeDrawer('bottom')
    if (drawer.right.isOpen) closeDrawer('right')
  }

  const menuItems = computed<MenuEntry[]>(() => {
    // Use `hasPermissionsAnywhere` (across-teams) — not
    // `hasPermissions` (current-tenant only). Platform-tier actors
    // (SA / system) live at tenant 0 but commonly operate via
    // `X-Operating-Tenant` impersonation; their tenant-scoped
    // permission list for tenant N doesn't reflect platform.*
    // grants. For nav visibility we want "does this person hold
    // the permission ANYWHERE in their identity", not "at the
    // current operating tenant". The across-teams probe matches
    // intent + bypasses for SA in `buildUser.isPlatformTier`.
    const permitted = (item: MenuItem): boolean =>
      !item.permissions ||
      item.permissions.length === 0 ||
      globalStore.user.hasPermissionsAnywhere(item.permissions)

    // Platform — what only the platform tier manages: tenants, the on-prem
    // clients this cloud manages, and the MDM profiles a flash station can
    // set a unit up for. Its own section at the bottom of the rail. Each
    // item is gated on its own permission (all three are in
    // superAdminGrants(); nobody else holds them — permission is the
    // contract, no role names here), and the section's divider shows only
    // when one of them does, so nobody else gets a rule with nothing under
    // it. On-Prem Clients and MDM Profiles are absent from an appliance's
    // own build: an appliance manages no appliances and runs no assembly
    // line. Their routes in authRoutes.ts are gated on the same flag, and
    // their policies refuse there.
    const platform = (
      [
        {
          name: t('nav.tenants'),
          href: 'Tenants',
          icon: 'pi-building',
          current: route.name === 'Tenants',
          permissions: [Permissions.PLATFORM_TENANTS_MANAGE],
        },
        ...(isStandaloneEnv
          ? []
          : [
              {
                name: t('nav.onPremClients'),
                href: 'OnPremClients',
                icon: 'pi-sitemap',
                current: route.name === 'OnPremClients',
                permissions: [Permissions.PLATFORM_APPLIANCES_MANAGE],
              },
              {
                name: t('nav.mdmProfiles'),
                href: 'MdmProfiles',
                icon: 'pi-shield',
                current: route.name === 'MdmProfiles',
                permissions: [Permissions.PLATFORM_MDM_PROFILES_MANAGE],
              },
            ]),
      ] satisfies MenuItem[]
    ).filter(permitted)

    const assetAccounting: MenuItem = {
      name: t('nav.assetAccounting'),
      href: 'AssetAccounting',
      icon: 'pi-dollar',
      current: route.name === 'AssetAccounting',
      permissions: [Permissions.TENANT_DEVICES_VIEW],
      children: (['overview', 'loss', 'inventory', 'repair', 'rules'] as const).map((v) => ({
        name: t(`assetAccounting.tabs.${v}`),
        href: 'AssetAccounting',
        query: { view: v },
        current:
          route.name === 'AssetAccounting' &&
          (typeof route.query.view === 'string' ? route.query.view : 'overview') === v,
      })),
    }

    const items: MenuEntry[] = [
      {
        name: t('nav.dashboard'),
        href: 'Dashboard',
        icon: 'pi-th-large',
        current: route.name === 'Dashboard',
      },
      {
        name: t('nav.missionControl'),
        href: 'MissionControl',
        icon: 'pi-compass',
        current: route.name === 'MissionControl',
        permissions: [Permissions.TENANT_KIOSKS_VIEW],
      },
      // Divider separating overview (Dashboard, Mission Control) from
      // the management surfaces below (Kiosks, Devices).
      { divider: true },
      {
        name: t('nav.kiosks'),
        href: 'KioskList',
        icon: 'pi-desktop',
        current: route.name === 'KioskList',
        permissions: [Permissions.TENANT_KIOSKS_VIEW],
      },
      {
        name: t('nav.devices'),
        href: 'Devices',
        icon: 'pi-tablet',
        // `Devices` is the parent route hosting both Inventory (default
        // tab on mobile / page on desktop) and Models management
        // (mobile tab / desktop slide-over drawer). Marked current
        // whenever the active route lives under that name.
        current:
          route.name === 'Devices' ||
          route.name === 'DeviceList' ||
          route.name === 'DeviceModelList',
        permissions: [Permissions.TENANT_DEVICES_VIEW],
      },
      {
        // Users section — single nav entry; UsersView's internal
        // tabs surface Groups + Guests when the actor has the
        // matching permissions. Legacy `GroupList` route name
        // redirects into the Groups tab on this same view.
        name: t('nav.users'),
        href: 'UserList',
        icon: 'pi-users',
        current: route.name === 'UserList' || route.name === 'GroupList',
        permissions: [Permissions.TENANT_USERS_VIEW],
      },
      {
        name: t('nav.bayAccess'),
        href: 'AccessMatrix',
        icon: 'pi-share-alt',
        current: route.name === 'AccessMatrix',
        permissions: [Permissions.TENANT_BAY_ACCESS_VIEW],
      },
      // Divider separating management surfaces (Kiosks/Devices/Users/
      // Access Matrix) from the operational/activity stream
      // (Notifications, Orders, Activity, System Logs, Reports).
      // Notifications leads the group — operator's daily inbox.
      { divider: true },
      {
        name: t('nav.notifications'),
        href: 'Notifications',
        icon: 'pi-bell',
        current: route.name === 'Notifications',
      },
      {
        // Tracker — unified historical trace surface. Replaces the
        // prior Orders + Activity Log read views. Permission gate is
        // STRICTER than the former Activity Log (TENANT_ACTIVITY_VIEW
        // = manager + admin) — Tracker's polymorphic cross-source
        // feed is tenant_admin-and-above only via TENANT_TRACKER_VIEW.
        name: t('nav.tracker'),
        href: 'Tracker',
        icon: 'pi-history',
        current: route.name === 'Tracker',
        permissions: [Permissions.TENANT_TRACKER_VIEW],
      },
      {
        // System Logs — engineering-tier surface. Gated on
        // `PLATFORM_VIEW_ALL` (held by super_admin via the policy
        // bypass; non-platform actors can be explicitly granted it
        // later without code changes). Role names stay out of the
        // FE — the permission is the contract.
        name: t('nav.systemLogs'),
        href: 'SystemLogs',
        icon: 'pi-file',
        current: route.name === 'SystemLogs',
        permissions: [Permissions.PLATFORM_VIEW_ALL],
      },
      {
        // Reports — expandable group (no chip). Three children target
        // distinct ROUTES (not `?view=`), so each child's `current` is a
        // plain route-name match and the parent is current on either.
        // Activity log is the default child (shares the parent `Reports`
        // route). Device status reuses `nav.deviceStatus`. Mechanism =
        // the Asset Accounting group above. The Activity log reads the
        // Tracker's trace + stats endpoints, so it carries the Tracker's
        // gate; for a manager the parent falls through to Device status.
        name: t('nav.reports'),
        href: 'Reports',
        icon: 'pi-chart-line',
        current: route.name === 'Reports' || route.name === 'DeviceStatusReport' || route.name === 'SyncLog',
        children: [
          {
            name: t('nav.activityLog'),
            href: 'Reports',
            current: route.name === 'Reports',
            permissions: [Permissions.TENANT_TRACKER_VIEW],
          },
          {
            name: t('nav.deviceStatus'),
            href: 'DeviceStatusReport',
            current: route.name === 'DeviceStatusReport',
            permissions: [Permissions.TENANT_DEVICES_VIEW],
          },
          {
            name: t('nav.syncLog'),
            href: 'SyncLog',
            current: route.name === 'SyncLog',
          },
        ],
      },
      // Finance — Asset Accounting. Expandable group: children deep-link to
      // the five panes via `?view=`, sub-item active-state tracks the active
      // view. Gated on the device view permission like its route
      // (authRoutes.ts) and the `/api/asset-accounting/*` reads; its leading
      // divider shows only with it, so a user without the grant doesn't get
      // two consecutive dividers (same shape as the platform section).
      ...(permitted(assetAccounting)
        ? ([{ divider: true }, assetAccounting] satisfies MenuEntry[])
        : []),
      // Divider separating the operational stream from the
      // admin/settings group at the bottom of the rail (OTA, Configs)
      // — visited rarely, tenant_admin-tier. OTA sits directly above
      // Configs per operator request.
      { divider: true },
      {
        name: t('nav.otaUpdates'),
        href: 'OtaUpdates',
        icon: 'pi-cloud-upload',
        current: route.name === 'OtaUpdates',
        permissions: [Permissions.TENANT_OTA_MANAGE],
      },
      {
        name: t('nav.configs'),
        href: 'Configs',
        icon: 'pi-cog',
        current: route.name === 'Configs',
      },
      ...(platform.length > 0 ? ([{ divider: true }, ...platform] satisfies MenuEntry[]) : []),
    ]

    return items.flatMap((item): MenuEntry[] => {
      // Dividers always render — no permission gating.
      if (isMenuDivider(item)) return [item]
      if (!permitted(item)) return []
      if (!item.children) return [item]
      // A group shows only the children the user may open, and its own
      // link falls through to the first of them when its default child
      // is hidden. A group left with no children is hidden.
      const children = item.children.filter(permitted)
      if (children.length === 0) return []
      const href = children.some((c) => c.href === item.href) ? item.href : children[0].href
      return [{ ...item, href, children }]
    })
  })

  return {
    sidebarMobileOpen,
    sidebarCompact,
    expandedMenus,
    navigating,
    drawer,
    drawerOpen,
    openSidebarMobile,
    closeSidebarMobile,
    toggleSidebarMobile,
    toggleSidebarCompact,
    toggleSidebar,
    toggleMenu,
    startNavigation,
    finishNavigation,
    openDrawer,
    closeDrawer,
    closeAllDrawers,
    onDrawerEvent,
    menuItems,
  }
})
